import { Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { delayWhen, first, map, tap } from 'rxjs';

import type { CorrelationId } from '@recruitee/ngrx';
import { CandidateShareActions, SharingContainerActions } from '@recruitee/sharing-container-data';
import type {
  SharingContainerApi,
  SharingContainerSaveData,
} from '@recruitee/sharing-container-types';
import { getDifferences } from '@recruitee/utils';

import type { ContainerChanges, State } from './candidate-sharing-dialog.types';

@Injectable({ providedIn: 'root' })
export class CandidateSharingDialogService {
  constructor(
    private store: Store,
    private actions$: Actions,
  ) {}

  public awaitSaveResult<
    SaveResult extends { container: SharingContainerApi } & CorrelationId,
    Callback extends (...args: any[]) => void,
  >(onSuccessOrFailure: Callback, ...args: Parameters<Callback>): Observable<SaveResult> {
    return this.actions$.pipe(
      ofType(SharingContainerActions.saveSuccess, SharingContainerActions.saveFailed),
      delayWhen(() =>
        this.actions$.pipe(
          ofType(
            CandidateShareActions.fetchSuccess,
            SharingContainerActions.fetchCandidateCollectionSuccess,
          ),
        ),
      ),
      tap(() => onSuccessOrFailure(...args)),
      first(action => action.type === SharingContainerActions.saveSuccess.type),
      map(action => <SaveResult>action.payload),
    );
  }

  public fetchContainers(stateSnapshot: State): void {
    if (stateSnapshot.deleteMode) {
      this.store.dispatch(
        SharingContainerActions.fetchCandidateCollection({
          payload: { candidateId: stateSnapshot.candidateId },
        }),
      );
    } else {
      const params = <const>{
        page: 1,
        limit: 1000,
        scope: 'active',
      };
      this.store.dispatch(
        SharingContainerActions.fetchCollection({
          payload: { params, scope: stateSnapshot.dataScope },
        }),
      );
    }
  }

  public saveCreatedContainers(
    changes: ContainerChanges,
    stateSnapshot: State,
  ): SharingContainerApi[] {
    const createdContainers = stateSnapshot.activeContainers.filter(container =>
      changes.created.includes(container.id),
    );
    createdContainers.forEach(container => {
      this.store.dispatch(
        SharingContainerActions.save({
          payload: {
            container: <SharingContainerSaveData>{
              ...container,
              candidateIds: [...(container.candidateIds ?? []), stateSnapshot.candidateId],
            },
          },
        }),
      );
    });
    return createdContainers;
  }

  public saveDeletedContainers(
    changes: ContainerChanges,
    stateSnapshot: State,
  ): SharingContainerApi[] {
    const deletedContainers = stateSnapshot.activeContainers.filter(container =>
      changes.deleted.includes(container.id),
    );
    deletedContainers.forEach(container => {
      container.candidateIds = container.candidateIds!.filter(
        (containerId: number) => containerId !== stateSnapshot.candidateId,
      );
      this.store.dispatch(
        SharingContainerActions.save({
          payload: {
            container: <SharingContainerSaveData>{
              ...container,
              candidateIds:
                container.candidateIds?.filter(
                  (containerId: number) => containerId !== stateSnapshot.candidateId,
                ) ?? [],
            },
          },
        }),
      );
    });
    return deletedContainers;
  }

  public getAssignmentsChanges(
    newAssignments: number[],
    currentAssignments: number[],
    deleteMode: boolean,
  ): { deleted: number[]; created: number[] } {
    if (deleteMode) {
      return {
        deleted: newAssignments,
        created: [],
      };
    }

    return {
      deleted: getDifferences(currentAssignments, newAssignments),
      created: getDifferences(newAssignments, currentAssignments),
    };
  }
}
